import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import _ from 'lodash';
import {
  AIRuntimeFindingsQueryParams,
  HygieneDefaultQueryParams,
  InventoryDefaultQueryParams,
  ViolationsDefaultQueryParams,
} from 'src/app/models/constants/navigation.constants';
import { HygieneFiltersModel } from 'src/app/models/filters/implementations/pageFilters/hygieneFilters.model';
import { InventoryFiltersModel } from 'src/app/models/filters/implementations/pageFilters/inventoryFilters.model';
import { PlaybookExecutionsFilters } from 'src/app/models/filters/implementations/pageFilters/playbookExecutionsFilters.model';
import { PlaybooksFiltersModel } from 'src/app/models/filters/implementations/pageFilters/playbooksFilters.model';
import { ViolationsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/violationsFilters.model';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { PlaybooksHttpServiceService } from '../httpServices/playbooksHttpService/playbooks-http-service.service';
import { InventoryHttpService } from '../httpServices/inventoryHttpService/inventory-http.service';
import { StoreService } from '../infra/store/store.service';
import { FilterStorage } from './filter-storage';
import { FiltersQueryTransformer } from './filtersQueryTransformer.ts/filtersQueryTransformer';
import { DashboardsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/dashboardsFilters.model';
import { LoggerService } from '../infra/logger/logger.service';
import { ViolationRulesHttpService } from '../httpServices/governance/violationRulesHttpService/violation-rules-http.service';
import { DetectionRulesHttpService } from '../httpServices/governance/detectionRulesHttpService/detection-rules-http.service';
import { FeatureFlagsService } from '../infra/featureFlags/feature-flags.service';
import { AiActivityFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiActivityFilters.model';
import { AiAgentStepsHttpService } from '../httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { AiFindingsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiFindingsFilters.model';
import { AiFindingsHttpService } from '../httpServices/detection/aiFindingsHttpService/ai-findings-http.service';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { AiDataInsightsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiDataInsightsFilters.model';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { ViolationRulesFiltersModel } from 'src/app/components/violation-rules/filters/violation-rules-filters.model';
import { InLineControlFiltersModel } from 'src/app/models/filters/implementations/pageFilters/inLineControlFilters.model';
import { SearchActivityHttpService } from '../httpServices/detection/searchActivityService/search-activity-http.service';
import { AiInLineActivityFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiInLineActivityFilters.model';
import { InLineIntegrationsHttpService } from '../httpServices/edgeIntegrationsHttpsService/in-line-integrations-http.service';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { AgentNameToLabelPipe } from 'src/app/pipes/agent-name-to-label.pipe';
import { PermissionsService } from '../infra/permissionsService/permissions.service';

interface RouteContext {
  routeSegment: string;
  tabKey: string | undefined;
  applyFilters: (filters: { [key: string]: string[] }) => void;
}

const DEFAULT_QUERY_MAP: Record<string, string> = {
  violations: ViolationsDefaultQueryParams,
  inventory: InventoryDefaultQueryParams,
  hygiene: HygieneDefaultQueryParams,
  'ai-findings': AIRuntimeFindingsQueryParams,
};

@Injectable({
  providedIn: 'root',
})
export class FiltersService {
  private readonly _violationsFilter: ViolationsFiltersModel;
  private readonly _inventoryFilters: InventoryFiltersModel;
  private readonly _hygieneFilter: HygieneFiltersModel;
  private readonly _playbooksFilter: PlaybooksFiltersModel;
  private readonly _playbookExecutionsFilter: PlaybookExecutionsFilters;
  private readonly _dashboardsFilter: DashboardsFiltersModel;
  private readonly _aiActivityFilter: AiActivityFiltersModel;
  private readonly _aiFindingsFilter: AiFindingsFiltersModel;
  private readonly _aiDataInsightsFilter: AiDataInsightsFiltersModel;
  private readonly _inLineControlFilter: InLineControlFiltersModel;
  private readonly _inLineActivityFilter: AiInLineActivityFiltersModel;
  private readonly _violationRulesFilter: ViolationRulesFiltersModel;

  private _lastFiltersFromQuery: string | undefined = undefined;
  private _lastTab = '';
  private _lastRoute = '';

  constructor(
    public featureFlagsService: FeatureFlagsService,
    private readonly _router: Router,
    _store: StoreService,
    _activatedRoute: ActivatedRoute,
    _logger: LoggerService,
    _violationRulesHttpService: ViolationRulesHttpService,
    _detectionRulesHttpService: DetectionRulesHttpService,
    _inventoryHttpService: InventoryHttpService,
    _playbooksHttpService: PlaybooksHttpServiceService,
    _aiActivityHttpService: AiAgentStepsHttpService,
    _aiFindingsHttpService: AiFindingsHttpService,
    _searchActivityHttpService: SearchActivityHttpService,
    _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
    _beautifyDisplayStringPipe: BeautifyDisplayStringPipe,
    _inLineIntegrationsHttpService: InLineIntegrationsHttpService,
    _labelsProviderPipe: LabelsProviderPipe,
    _agentNameToLabelPipe: AgentNameToLabelPipe,
    _permissionsService: PermissionsService
  ) {
    this._violationsFilter = new ViolationsFiltersModel(
      _router,
      _store,
      _violationRulesHttpService,
      _inventoryHttpService,
      featureFlagsService,
      _beautifyDisplayStringPipe,
      _permissionsService
    );
    this._inventoryFilters = new InventoryFiltersModel(
      _router,
      _store,
      _inventoryHttpService,
      featureFlagsService,
      _beautifyDisplayStringPipe,
      _permissionsService
    );
    this._hygieneFilter = new HygieneFiltersModel(
      _router,
      _store,
      _violationRulesHttpService,
      _inventoryHttpService,
      featureFlagsService,
      _beautifyDisplayStringPipe,
      _permissionsService
    );
    this._playbooksFilter = new PlaybooksFiltersModel(_router, _store, _permissionsService);
    this._playbookExecutionsFilter = new PlaybookExecutionsFilters(
      _router,
      _store,
      _playbooksHttpService,
      _logger,
      featureFlagsService,
      _permissionsService
    );
    this._dashboardsFilter = new DashboardsFiltersModel(_router, _store, _inventoryHttpService, _permissionsService);
    this._aiActivityFilter = new AiActivityFiltersModel(
      _router,
      _store,
      _aiActivityHttpService,
      featureFlagsService,
      _displayCamelCaseStringPipe,
      _permissionsService
    );
    this._aiFindingsFilter = new AiFindingsFiltersModel(
      _router,
      _store,
      _aiActivityHttpService,
      _aiFindingsHttpService,
      featureFlagsService,
      _permissionsService
    );
    this._aiDataInsightsFilter = new AiDataInsightsFiltersModel(_router, _store, _permissionsService);
    this._inLineControlFilter = new InLineControlFiltersModel(
      _router,
      _store,
      _inLineIntegrationsHttpService,
      _permissionsService
    );
    this._inLineActivityFilter = new AiInLineActivityFiltersModel(
      _router,
      _store,
      _labelsProviderPipe,
      _agentNameToLabelPipe,
      _permissionsService
    );
    this._violationRulesFilter = new ViolationRulesFiltersModel(_router);
  }

  public getFilters(filterType: PageWithFiltersEnum) {
    switch (filterType) {
      case PageWithFiltersEnum.Violations:
        return this._violationsFilter;

      case PageWithFiltersEnum.Inventory:
        return this._inventoryFilters;

      case PageWithFiltersEnum.Hygiene:
        return this._hygieneFilter;

      case PageWithFiltersEnum.Playbooks:
        return this._playbooksFilter;

      case PageWithFiltersEnum.Executions:
        return this._playbookExecutionsFilter;

      case PageWithFiltersEnum.Dashboards:
        return this._dashboardsFilter;

      case PageWithFiltersEnum.AiActivity:
        return this._aiActivityFilter;

      case PageWithFiltersEnum.AiFindings:
        return this._aiFindingsFilter;

      case PageWithFiltersEnum.AiDataInsights:
        return this._aiDataInsightsFilter;

      case PageWithFiltersEnum.InLineControl:
        return this._inLineControlFilter;

      case PageWithFiltersEnum.InLineActivity:
        return this._inLineActivityFilter;

      case PageWithFiltersEnum.Policies:
        return this._violationRulesFilter;

      default:
        throw new Error(`FiltersService getFilters: Missing support for ${filterType}`);
    }
  }

  public updateFiltersFromQuery(filters?: string, tab?: string) {
    const currentRoute = this._router.url.split('?')[0];
    const routeChanged = currentRoute !== this._lastRoute;
    this._lastRoute = currentRoute;

    if (routeChanged) {
      if (currentRoute.includes('violations')) {
        this._violationsFilter.resetDefaults();
      }
      if (currentRoute.includes('hygiene')) {
        this._hygieneFilter.resetIntegrationStatusDefault();
      }
    }

    // Restore from sessionStorage on route change
    if (routeChanged) {
      const ctx = this._resolveRouteContext(tab);
      if (ctx) {
        const isDefaultOrEmpty = !filters || filters === '' || this._isDefaultQuery(ctx.routeSegment, filters);

        if (isDefaultOrEmpty) {
          const saved = FilterStorage.load(ctx.routeSegment, ctx.tabKey);
          if (saved !== null) {
            filters = saved;
          }
        }
      }
    }

    if (filters === undefined || filters == '') {
      if (filters !== this._lastFiltersFromQuery || routeChanged) {
        const ctx = this._resolveRouteContext(tab);
        if (ctx) {
          ctx.applyFilters({});
          FilterStorage.save(ctx.routeSegment, '', ctx.tabKey);
        }
      }
      this._lastFiltersFromQuery = filters ?? '';
      return;
    }

    const filtersAfterTrim = filters.trim();

    if (filtersAfterTrim === this._lastFiltersFromQuery && tab === this._lastTab) {
      return;
    }
    const queryAsDict = FiltersQueryTransformer.transformFromQuery(filters);
    this._lastFiltersFromQuery = filtersAfterTrim;
    this._lastTab = tab ? tab : '';

    if (Object.keys(queryAsDict).length > 0) {
      const ctx = this._resolveRouteContext(tab);
      if (ctx) {
        ctx.applyFilters(queryAsDict);
        FilterStorage.save(ctx.routeSegment, filtersAfterTrim, ctx.tabKey);
      }
    }
  }

  private _resolveRouteContext(tab?: string): RouteContext | undefined {
    const url = this._router.url;

    if (url.includes('violations')) {
      return {
        routeSegment: 'violations',
        tabKey: undefined,
        applyFilters: (f) => this._violationsFilter.updateFiltersByKeyValue(f),
      };
    }
    if (url.includes('inventory')) {
      return {
        routeSegment: 'inventory',
        tabKey: undefined,
        applyFilters: (f) => this._inventoryFilters.updateFiltersByKeyValue(f),
      };
    }
    if (url.includes('hygiene')) {
      return {
        routeSegment: 'hygiene',
        tabKey: undefined,
        applyFilters: (f) => this._hygieneFilter.updateFiltersByKeyValue(f),
      };
    }
    if (url.includes('playbooks')) {
      if (tab === 'executionHistory') {
        return {
          routeSegment: 'playbooks',
          tabKey: 'executionHistory',
          applyFilters: (f) => this._playbookExecutionsFilter.updateFiltersByKeyValue(f),
        };
      }
      if (tab === 'yourPlaybooks') {
        return {
          routeSegment: 'playbooks',
          tabKey: 'yourPlaybooks',
          applyFilters: (f) => this._playbooksFilter.updateFiltersByKeyValue(f),
        };
      }
      return undefined;
    }
    if (url.includes('ai-activity')) {
      if (url.includes('inline=true')) {
        return {
          routeSegment: 'ai-activity',
          tabKey: 'inline',
          applyFilters: (f) => this._inLineActivityFilter.updateFiltersByKeyValue(f),
        };
      }
      return {
        routeSegment: 'ai-activity',
        tabKey: undefined,
        applyFilters: (f) => this._aiActivityFilter.updateFiltersByKeyValue(f),
      };
    }
    if (url.includes('ai-findings')) {
      return {
        routeSegment: 'ai-findings',
        tabKey: undefined,
        applyFilters: (f) => this._aiFindingsFilter.updateFiltersByKeyValue(f),
      };
    }
    if (url.includes('ai-data-insights')) {
      return {
        routeSegment: 'ai-data-insights',
        tabKey: undefined,
        applyFilters: (f) => this._aiDataInsightsFilter.updateFiltersByKeyValue(f),
      };
    }
    if (url.includes('ai-in-line-control')) {
      return {
        routeSegment: 'ai-in-line-control',
        tabKey: undefined,
        applyFilters: (f) => this._inLineControlFilter.updateFiltersByKeyValue(f),
      };
    }
    if (url.includes('policies')) {
      return {
        routeSegment: 'policies',
        tabKey: undefined,
        applyFilters: (f) => this._violationRulesFilter.updateFiltersByKeyValue(f),
      };
    }
    return undefined;
  }

  private _isDefaultQuery(routeSegment: string, q: string): boolean {
    const defaultQ = DEFAULT_QUERY_MAP[routeSegment];
    if (!defaultQ) {
      return false;
    }
    const incoming = FiltersQueryTransformer.transformFromQuery(q);
    const defaults = FiltersQueryTransformer.transformFromQuery(defaultQ);
    return _.isEqual(incoming, defaults);
  }
}
